import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Formik } from "formik";
import * as yup from "yup";
import { useEffect, useState } from "react";
import Header from "../../components/Header";
import LoadingSpinner from "../../loadingSpinner";
import ModalSucces from "../../modal/modalSucces";
import ModalError from "../../modal/modalError";
import ModalCharge from "../../modal/modalCharge";
import {
  createAccesoEvento,
  fetchAccesoEventoById,
  fetchAccesoEventos,
  updateAccesoEvento,
} from "../../services/accesoEventos.services";

const initialValues = {
  id: "",
  nombre_evento: "",
  endpoint: "",
  door_ip: "",
  esp_ip: "",
  marcacion_habilitada: 1,
  activo: 1,
  digest_user: "",
  digest_password: "",
  delay_ms: "0",
  cooldown_ms: "0",
};

const validationSchema = yup.object().shape({
  nombre_evento: yup.string().required("Nombre del evento requerido"),
  endpoint: yup.string().required("Endpoint requerido"),
  door_ip: yup.string().required("Door IP requerida"),
  esp_ip: yup
    .string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .notRequired(),
  digest_user: yup.string().required("Usuario digest requerido"),
  digest_password: yup.string().required("Password digest requerida"),
  delay_ms: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? 0 : value
    )
    .min(0, "Debe ser positivo")
    .required("Delay requerido"),
  cooldown_ms: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? 0 : value
    )
    .min(0, "Debe ser positivo")
    .required("Cooldown requerido"),
  marcacion_habilitada: yup
    .number()
    .oneOf([0, 1], "Seleccion invalida")
    .required("Seleccion requerida"),
  activo: yup
    .number()
    .oneOf([0, 1], "Seleccion invalida")
    .required("Seleccion requerida"),
});

const toBinary = (value, fallback) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  if (value === true) {
    return 1;
  }
  if (value === false) {
    return 0;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? fallback : numeric;
};

const toNonNegativeNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return fallback;
  }
  return numeric;
};


const normalizeAccessPoint = (item = {}) => {
  const toStringSafe = (value, fallback = "") => {
    if (value === null || value === undefined) {
      return fallback;
    }
    return String(value);
  };

  const toStringValue = (value, fallback = "0") => {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }
    if (typeof value === "number") {
      return String(value);
    }
    return String(value);
  };

  const idCandidate =
    item.id ??
    item.id_acceso ??
    item.id_acceso_evento ??
    item.idAcceso ??
    item.acceso_evento_id ??
    "";

  return {
    id:
      idCandidate === null || idCandidate === undefined
        ? ""
        : String(idCandidate),
    nombre_evento:
      item.nombre_evento ?? item.nombreEvento ?? item.evento ?? "",
    endpoint: item.endpoint ?? item.endpoint_name ?? "",
    door_ip: item.door_ip ?? item.doorIp ?? item.puerta_ip ?? "",
    esp_ip: item.esp_ip ?? item.espIp ?? item.endpoint_ip ?? "",
    marcacion_habilitada: toBinary(
      item.marcacion_habilitada ?? item.marcacionHabilitada ?? item.marcacion,
      0
    ),
    activo: toBinary(item.activo ?? item.isActive ?? item.estado, 1),
    digest_user: toStringSafe(item.digest_user ?? item.digestUser, ""),
    digest_password: toStringSafe(
      item.digest_password ?? item.digestPassword,
      ""
    ),
    delay_ms: toStringValue(
      item.delay_ms ?? item.delayMs ?? item.delay ?? item.delayMilliseconds,
      "0"
    ),
    cooldown_ms: toStringValue(
      item.cooldown_ms ??
        item.cooldownMs ??
        item.cooldown ??
        item.cooldownMilliseconds,
      "0"
    ),
  };
};

const AccesoEventos = () => {
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [accessPoints, setAccessPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchId, setSearchId] = useState("");

  const refreshList = async () => {
    setTableLoading(true);
    try {
      const data = await fetchAccesoEventos();
      const collection = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];
      setAccessPoints(collection.map((item) => normalizeAccessPoint(item)));
    } catch (error) {
      setErrorMessage(error.message || "No se pudo obtener la lista");
      setErrorModal(true);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    refreshList();
  }, []);

  const handleSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    const cleanEspIp =
      typeof values.esp_ip === "string" ? values.esp_ip.trim() : "";

    const delayMs = toNonNegativeNumber(values.delay_ms, 0);
    const cooldownMs = toNonNegativeNumber(values.cooldown_ms, 0);

    const payload = {
      nombre_evento: values.nombre_evento.trim(),
      endpoint: values.endpoint.trim(),
      door_ip: values.door_ip.trim(),
      esp_ip: cleanEspIp === "" ? null : cleanEspIp,
      marcacion_habilitada: toBinary(values.marcacion_habilitada, 0),
      activo: toBinary(values.activo, 1),
      digest_user: values.digest_user.trim(),
      digest_password: values.digest_password.trim(),
      delay_ms: delayMs,
      cooldown_ms: cooldownMs,
    };

    try {
      if (values.id) {
        await updateAccesoEvento(values.id, payload);
      } else {
        await createAccesoEvento(payload);
        resetForm({ values: initialValues });
        setSearchId("");
      }
      await refreshList();
      setSuccessModal(true);
    } catch (error) {
      setErrorMessage(error.message || "No se pudo guardar el registro");
      setErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (resetForm) => {
    if (!searchId.trim()) {
      setErrorMessage("Ingrese un identificador para buscar");
      setErrorModal(true);
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchAccesoEventoById(searchId.trim());
      const match = Array.isArray(data) && data.length ? data[0] : data;
      const normalized = normalizeAccessPoint(match);
      resetForm({ values: { ...initialValues, ...normalized } });
      if (normalized.id) {
        setSearchId(normalized.id);
      }
    } catch (error) {
      setErrorMessage(error.message || "No se encontro el registro solicitado");
      setErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRow = (row, resetForm) => {
    const normalized = normalizeAccessPoint(row);
    resetForm({ values: { ...initialValues, ...normalized } });
    setSearchId(normalized.id);
  };

  const handleReset = (resetForm) => {
    resetForm({ values: initialValues });
    setSearchId("");
  };

  return (
    <Box m="20px">
      <Header
        title="Registro Accesos"
        subtitle="Gestion de puntos de acceso fisico"
      />

      {isLoading && <LoadingSpinner />}
      {successModal && (
        <ModalSucces open={successModal} onClose={() => setSuccessModal(false)} />
      )}
      {errorModal && (
        <ModalError
          open={errorModal}
          onClose={() => setErrorModal(false)}
          error={errorMessage}
        />
      )}

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          resetForm,
        }) => (
          <>
          <form onSubmit={handleSubmit}>
            <Box mb={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  label="Buscar por ID"
                  variant="filled"
                  value={searchId}
                  onChange={(event) => setSearchId(event.target.value)}
                  sx={{ width: { xs: "100%", md: "220px" } }}
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleSearch(resetForm)}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleReset(resetForm)}
                  >
                    Limpiar
                  </Button>
                </Stack>
                <Typography color={theme.palette.secondary.main}>
                  {values.id
                    ? `Editando acceso ID ${values.id}`
                    : "Creando nuevo acceso"}
                </Typography>
              </Stack>
            </Box>

            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{ "& > div": { gridColumn: isNonMobile ? undefined : "span 4" } }}
            >
              <TextField
                fullWidth
                label="ID"
                variant="filled"
                name="id"
                value={values.id}
                disabled
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="Nombre del evento"
                variant="filled"
                name="nombre_evento"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.nombre_evento}
                error={!!touched.nombre_evento && !!errors.nombre_evento}
                helperText={touched.nombre_evento && errors.nombre_evento}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="Endpoint"
                variant="filled"
                name="endpoint"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.endpoint}
                error={!!touched.endpoint && !!errors.endpoint}
                helperText={touched.endpoint && errors.endpoint}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="Door IP"
                variant="filled"
                name="door_ip"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.door_ip}
                error={!!touched.door_ip && !!errors.door_ip}
                helperText={touched.door_ip && errors.door_ip}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="ESP IP"
                variant="filled"
                name="esp_ip"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.esp_ip}
                error={!!touched.esp_ip && !!errors.esp_ip}
                helperText={touched.esp_ip && errors.esp_ip}
                sx={{ gridColumn: "span 2" }}
              />
                            <TextField
                fullWidth
                label="Digest user"
                variant="filled"
                name="digest_user"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.digest_user}
                error={!!touched.digest_user && !!errors.digest_user}
                helperText={touched.digest_user && errors.digest_user}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="Digest password"
                variant="filled"
                type="password"
                name="digest_password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.digest_password}
                error={!!touched.digest_password && !!errors.digest_password}
                helperText={touched.digest_password && errors.digest_password}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="Delay (ms)"
                variant="filled"
                type="number"
                name="delay_ms"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.delay_ms}
                error={!!touched.delay_ms && !!errors.delay_ms}
                helperText={touched.delay_ms && errors.delay_ms}
                inputProps={{ min: 0 }}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                label="Cooldown (ms)"
                variant="filled"
                type="number"
                name="cooldown_ms"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.cooldown_ms}
                error={!!touched.cooldown_ms && !!errors.cooldown_ms}
                helperText={touched.cooldown_ms && errors.cooldown_ms}
                inputProps={{ min: 0 }}
                sx={{ gridColumn: "span 2" }}
              />
              <FormControl variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="marcacion-label">Marcacion habilitada</InputLabel>
                <Select
                  labelId="marcacion-label"
                  name="marcacion_habilitada"
                  value={values.marcacion_habilitada}
                  onChange={handleChange}
                >
                  <MenuItem value={1}>Si</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="activo-label">Activo</InputLabel>
                <Select
                  labelId="activo-label"
                  name="activo"
                  value={values.activo}
                  onChange={handleChange}
                >
                  <MenuItem value={1}>Si</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" color="secondary">
                Guardar
              </Button>
            </Box>

            <ModalCharge isLoading={isLoading} />
          </form>

          <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={2}
              py={2}
            >
              <Typography variant="h6">Accesos configurados</Typography>
              <Button variant="outlined" size="small" onClick={refreshList}>
                Refrescar
              </Button>
            </Box>
            {tableLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Typography>Cargando accesos...</Typography>
              </Box>
            ) : accessPoints.length === 0 ? (
              <Box px={2} pb={2}>
                <Typography>No hay accesos registrados.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Door IP</TableCell>
                    <TableCell>ESP IP</TableCell>
                    <TableCell>Digest user</TableCell>
                    <TableCell>Digest password</TableCell>
                    <TableCell>Delay (ms)</TableCell>
                    <TableCell>Cooldown (ms)</TableCell>
                    <TableCell>Marcacion</TableCell>
                    <TableCell>Activo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accessPoints.map((row) => {
                    const key = row.id || `${row.endpoint}-${row.door_ip}`;
                    return (
                      <TableRow
                        key={key}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSelectRow(row, resetForm)}
                      >
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.nombre_evento}</TableCell>
                        <TableCell>{row.endpoint}</TableCell>
                        <TableCell>{row.door_ip}</TableCell>
                        <TableCell>{row.esp_ip || "Sin configurar"}</TableCell>
                        <TableCell>{row.digest_user}</TableCell>
                        <TableCell>
                          {row.digest_password
                            ? "*".repeat(Math.min(row.digest_password.length, 6))
                            : ""}
                        </TableCell>
                        <TableCell>{row.delay_ms}</TableCell>
                        <TableCell>{row.cooldown_ms}</TableCell>
                        <TableCell>{row.marcacion_habilitada ? "Si" : "No"}</TableCell>
                        <TableCell>{row.activo ? "Si" : "No"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          </>
        )}
      </Formik>
    </Box>
  );
};

export default AccesoEventos;
